<template>
  <Skeleton active :loading="skeletonLoading">
    <Pagination
      v-if="showTopPagination && dataList.length"
      v-model="pagination.current"
      :total="pagination.total"
      show-size-changer
      showQuickJumper
      :page-size.sync="pagination.pageSize"
    />
    <Spin :spinning="loading">
      <div class="list">
        <slot>
          <div class="item" v-for="item in dataList" :key="item.id">
            <div class="timeBox">
              <div class="time">
                <div>{{ dayjs(item.publishTime).format('MM') }}月</div>
                <div class="day">{{ dayjs(item.publishTime).format('DD') }}</div>
                <div>{{ dayjs(item.publishTime).format('YYYY') }}</div>
              </div>
            </div>
            <div class="cover" :class="{ hide: hideImg[item.id] }">
              <PreviewImage
                class="img"
                :src="item.coverImage"
                :hover="true"
                :errorHide="true"
                @click="onClick(item)"
                @error="handleImgError(item)"
              />
            </div>
            <div class="info">
              <div class="title" :title="item.title" @click="onClick(item)">{{ item.title }}</div>
              <div class="text" @click="onClick(item)">{{ getHTMLText(item.content) }}</div>
            </div>
            <div class="other">
              <div class="type" @click.stop="onClickTag(item)">{{ getNoticeTagName(item.noticeTag) }}</div>
              <div class="icon">
                <img src="@/assets/com/eye.svg" />
                <span>{{ item.views }}</span>
              </div>
            </div>
          </div>
          <Empty v-if="dataList.length === 0" />
        </slot>
      </div>
    </Spin>
    <Pagination
      v-model="pagination.current"
      :total="pagination.total"
      show-size-changer
      showQuickJumper
      :page-size.sync="pagination.pageSize"
    />
  </Skeleton>
</template>
<script setup lang="ts">
import Vue, { reactive, ref, watch } from 'vue'
import { Skeleton, Spin, Pagination, Empty } from 'ant-design-vue'
import useList from '@/hooks/useList'
import { getNoticeList } from '@/api/notice'
import routerByNoticeTag, { getNoticeTagName } from '@/utils/noticeTag'
import { toDetail } from '@/utils'
import dayjs from 'dayjs'
import useInit from '@/hooks/useInit'
import { getHTMLText } from '@/utils/index'

type Props = {
  /** 显示顶部的分页器 */
  showTopPagination?: boolean
  /** 列表所查询的`noticeTag` */
  noticeTags: string
}

const props = withDefaults(defineProps<Props>(), {
  showTopPagination: true,
})

const { dataList, loading, pagination, onQueryList } = useList<NoticeItem>({
  getList: getNoticeList,
  formatGetListQuery(query) {
    return {
      ...query,
      noticeTags: props.noticeTags,
      /** 排序方式 1 创建时间 2 发布时间倒序 3 浏览量 4 发布时间正序 */
      sortBy: 2,
    }
  },
})

const { loading: skeletonLoading } = useInit(dataList)

watch(
  () => [pagination.current, pagination.pageSize],
  () => {
    onQueryList()
  }
)

function onClickTag(item: NoticeItem) {
  routerByNoticeTag(item.noticeTag)
}

function onClick(item: NoticeItem) {
  toDetail(item)
}

const hideImg = reactive<Record<string, boolean>>({})
function handleImgError(item: NoticeItem) {
  Vue.set(hideImg, item.id, true)
}
</script>
<style lang="less" scoped>
.item {
  display: flex;
  padding: 35px 0 30px 0;
  .img {
    transition: all 0.2s;
  }
  &:hover {
    .cover {
      .img {
        transform: scale(1.2);
      }
    }
  }

  .timeBox {
    display: flex;
    align-items: center;
    .time {
      display: flex;
      flex-direction: column;
      align-items: center;
      color: @success;
      font-size: 12px;
      border: 1px solid @success;
      padding: 15px 8px;
      border-radius: 15px;
      margin-right: 25px;
      .day {
        font-size: 26px;
        font-weight: 900;
      }
    }
  }

  .cover {
    width: 200px;
    height: 140px;
    margin-right: 30px;
    cursor: pointer;
    overflow: hidden;
    &.hide {
      display: none;
    }
    img {
      width: 100%;
      height: 100%;
      max-width: 120%;
      object-fit: cover;
      transition: all 0.2s;
    }
  }

  .info {
    flex: 1;
    padding-right: 30px;
    border-right: 1px solid #e1dbe5;
    .title {
      color: #414141;
      font-size: 18px;
      line-height: 24px;
      padding-bottom: 11px;
      cursor: pointer;
    }
    .text {
      color: #8d8d8d;
      line-height: 20px;
      padding-right: 0;
      word-break: break-all;
      word-wrap: break-word;
      font-size: 12px;
      cursor: pointer;
      .multi-ellipsis(2);
    }
  }

  .other {
    width: 98px;
    padding: 0 25px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    .type {
      color: @primary;
      font-size: 12px;
      margin-bottom: 5px;
      cursor: pointer;
    }
    .icon {
      color: #ababab;
      font-size: 12px;
      display: flex;
      align-items: center;
      img {
        width: 16px;
        height: 16px;
      }
    }
  }
}
</style>
